import {useSyncExternalStore} from 'react'
import {useForm, useFormSelector} from '@eitje/form'
import {Popout} from '~/components/popout'
import {DeleteButton, SubmitButton} from '~/components/eitje_button'
import {t} from '@eitje/bridge'
import {useHotkeys} from 'react-hotkeys-hook'
import utils from '@eitje/utils'

const FormSubmitButton = props => {
	'use no memo'
	const contextForm = useForm()
	const formSource = props.getData ? props : contextForm

	return (
		<ResolvedFormSubmitButton
			{...props}
			submit={formSource.submit}
			getData={formSource.getData}
			form={formSource.form}
			newForm={formSource.newForm}
		/>
	)
}

// NewForm modal footers render outside the form tree, so they subscribe to the form instance directly.
// Legacy modal footers and in-tree submit buttons keep using the prop/context-driven path above.
export const NewFormModalSubmitButton = props => {
	'use no memo'
	const {form} = props
	const snapshot = useSyncExternalStore(form.subscribe, form.getSnapshot, form.getSnapshot)

	return <SubmitButtonInnerBase {...props} formData={snapshot.fields} />
}

const FormDeleteButton = props => {
	const {submit} = useForm()
	const children = props.children || t('common.delete')
	return (
		<DeleteButton onDel={submit} {...props}>
			{children}
		</DeleteButton>
	)
}

const ResolvedFormSubmitButton = props => {
	const {newForm, ...rest} = props
	if (newForm) return <NewFormContextSubmitButtonInner {...rest} />

	return <LegacyFormSubmitButtonInner {...rest} />
}

const LegacyFormSubmitButtonInner = props => {
	const {getData} = props
	if (!getData) return null

	return <SubmitButtonInnerBase {...props} formData={getData()} />
}

const NewFormContextSubmitButtonInner = props => {
	'use no memo'
	const fields = useFormSelector('fields')
	// useFormSelector('registeredFields')

	return <SubmitButtonInnerBase {...props} formData={fields} />
}

const SubmitButtonInnerBase = props => {
	'use no memo'
	const {submitConfirmation, onClick, children: buttonChildren, ...submitButtonProps} = props
	const {submit, form, className, ButtonComponent = SubmitButton, isDisabled, formData, disabled, t: translation, text} = submitButtonProps
	const requiredString = t('form.required')
	const {errs = {}} = form?.validateInner?.() || {}
	const missingRequiredFields = Object.values(errs).some(e => e === requiredString)

	const handleSubmit = async submitOptions => {
		await flushFocusedElement()
		return submit(submitOptions)
	}

	useHotkeys('meta+enter', handleSubmit)
	const children = translation ? null : buttonChildren || text || t('common.submit')
	const _disabled = isDisabled ? isDisabled(formData) : disabled
	const classNames = utils.makeCns(className, 'form-submit-button')
	const showSubmitConfirmation = submitConfirmation && !missingRequiredFields
	const submitButtonOnClick = showSubmitConfirmation ? undefined : onClick || handleSubmit
	const missingRequiredFieldsTooltip = missingRequiredFields && 'form.general.tooltips.missing_required_fields'
	const submitButtonPopoutTitle = missingRequiredFieldsTooltip || submitButtonProps.popoutTitle

	const submitButton = (
		<ButtonComponent
			baseColor={missingRequiredFields ? 'grey' : 'blue'}
			onClick={submitButtonOnClick}
			onConfirm={handleSubmit}
			data-intercom-target="form-submit-button"
			className={classNames}
			{...submitButtonProps}
			disabled={_disabled}
			missingRequiredFields={missingRequiredFields}
			popoutTitle={submitButtonPopoutTitle}
		>
			{children}
		</ButtonComponent>
	)

	let confirmedSubmitButton = submitButton
	if (showSubmitConfirmation) {
		confirmedSubmitButton = (
			<Popout
				type="confirm"
				trigger={submitButton}
				onConfirm={handleSubmit}
				body={submitConfirmation.text}
				title={submitConfirmation.title}
				confirmButtonProps={submitConfirmation.confirmButtonProps}
				disabled={_disabled}
			/>
		)
	}

	return confirmedSubmitButton
}

// TimePicker commits panel selections on blur/close, so force that flush before submit reads form values.
const flushFocusedElement = async () => {
	if (typeof document === 'undefined') return

	const activeElement = document.activeElement
	if (!activeElement || activeElement === document.body || typeof activeElement.blur !== 'function') return

	activeElement.blur()
	await new Promise(resolve => setTimeout(resolve, 0))
}

export {FormSubmitButton, FormDeleteButton}
